import { ReactNode } from 'react';
import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { neutral } from '../../theme/colors';
import { palette } from './report-theme';

export interface ReportColumn<T> {
	title: string;
	align?: 'left' | 'right';
	/** Renders the cell. Keep numbers right-aligned and tabular. */
	render: (row: T, index: number) => ReactNode;
	width?: number | string;
}

interface ReportTableProps<T> {
	columns: ReportColumn<T>[];
	rows: T[];
	emptyMessage?: string;
	maxHeight?: number;
}

export const ReportTable = <T,>({ columns, rows, emptyMessage = 'No rows.', maxHeight }: ReportTableProps<T>) => (
	<Box sx={{ overflowX: 'auto', maxHeight, overflowY: maxHeight ? 'auto' : undefined }}>
		<Table size="small" stickyHeader={Boolean(maxHeight)} sx={{ minWidth: 420 }}>
			<TableHead>
				<TableRow>
					{columns.map((column) => (
						<TableCell
							key={column.title}
							align={column.align ?? 'left'}
							sx={{
								borderBottom: `1px solid ${neutral[200]}`,
								backgroundColor: '#fff',
								color: 'text.secondary',
								fontWeight: 600,
								fontSize: 12,
								letterSpacing: '0.04em',
								textTransform: 'uppercase',
								whiteSpace: 'nowrap',
								width: column.width,
								py: 1.25,
							}}
						>
							{column.title}
						</TableCell>
					))}
				</TableRow>
			</TableHead>
			<TableBody>
				{rows.length === 0 && (
					<TableRow>
						<TableCell colSpan={columns.length} sx={{ borderBottom: 'none', py: 4 }}>
							<Typography variant="body2" color="text.secondary" align="center">
								{emptyMessage}
							</Typography>
						</TableCell>
					</TableRow>
				)}
				{rows.map((row, index) => (
					<TableRow key={index} hover>
						{columns.map((column) => (
							<TableCell
								key={column.title}
								align={column.align ?? 'left'}
								sx={{
									borderBottom: `1px solid ${neutral[100]}`,
									py: 1.25,
									fontVariantNumeric: column.align === 'right' ? 'tabular-nums' : undefined,
									whiteSpace: 'nowrap',
								}}
							>
								{column.render(row, index)}
							</TableCell>
						))}
					</TableRow>
				))}
			</TableBody>
		</Table>
	</Box>
);

/** A share-of-total cell: the bar carries the comparison, the number the value. */
export const ShareCell = ({ share }: { share: number }) => (
	<Stack direction="row" alignItems="center" spacing={1} justifyContent="flex-end">
		<Box sx={{ width: 72, height: 6, borderRadius: 999, backgroundColor: neutral[100], overflow: 'hidden' }}>
			<Box
				sx={{
					width: `${Math.min(100, Math.max(0, share))}%`,
					height: '100%',
					borderRadius: 999,
					backgroundColor: palette.single,
				}}
			/>
		</Box>
		<Typography variant="body2" sx={{ width: 44, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
			{`${share.toFixed(1)}%`}
		</Typography>
	</Stack>
);
